import { useCallback, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Loader2, XCircle } from 'lucide-react';
import { Button, Checkbox, useToast } from '@/components/ui';
import { useCancelAskUser } from '@/hooks/useInbox';
import type { InboxItem, InboxItemStatus } from '@/types/inbox';

const HOTKEY_GROUP = { group: 'Inbox' };

interface InboxBulkActionBarProps {
  selected: InboxItem[];
  total: number;
  onClear: () => void;
  onSelectAll: () => void;
  /** Active list filter so cache patching targets the right query. */
  filter?: { status?: InboxItemStatus | 'all' };
  /**
   * Slot for future bulk verbs (Move, Mark read, …). Rendered between the
   * built-in Cancel button and the Clear button so the layout grows in place.
   */
  extraActions?: React.ReactNode;
}

export function InboxBulkActionBar({
  selected,
  total,
  onClear,
  onSelectAll,
  filter,
  extraActions,
}: InboxBulkActionBarProps) {
  const toast = useToast();
  const cancel = useCancelAskUser(filter);
  const [isCancelling, setIsCancelling] = useState(false);

  const count = selected.length;
  const canCancel = count > 0 && !isCancelling;
  const allSelected = total > 0 && count === total;
  const someSelected = count > 0 && !allSelected;

  // A partial (indeterminate) selection completes to "all" on press — only a
  // full selection clears. This keeps the control usable as a real select-all
  // rather than a deselect-only shortcut.
  const toggleAll = useCallback(() => {
    if (allSelected) onClear();
    else onSelectAll();
  }, [allSelected, onClear, onSelectAll]);

  const cancelAll = useCallback(async () => {
    if (selected.length === 0) return;
    setIsCancelling(true);
    let ok = 0;
    let fail = 0;
    // Run sequentially via the mutation hook so each success patches the
    // list cache (drops the row) — the API isn't bulk-aware.
    for (const item of selected) {
      try {
        // spaceId may be missing on legacy / non-space runs; the API will
        // surface that as an error and we'll count it as a failure.
        await cancel.mutateAsync({
          spaceId: item.spaceId ?? '',
          requestId: item.refId,
          inboxItemId: item.inboxItemId,
          reason: 'user_dismissed',
        });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setIsCancelling(false);
    const items = (n: number) => `${n} ${n === 1 ? 'item' : 'items'}`;
    if (ok > 0 && fail === 0) {
      toast.success(`Dismissed ${items(ok)}`);
    } else if (ok > 0 && fail > 0) {
      toast.success(`Dismissed ${items(ok)}, ${fail} skipped`);
    } else if (fail > 0) {
      toast.error(`Couldn't dismiss ${items(fail)}`);
    }
    onClear();
  }, [selected, cancel, toast, onClear]);

  useHotkeys(
    'shift+x',
    (event) => {
      event.preventDefault();
      toggleAll();
    },
    { description: 'Toggle select all', metadata: HOTKEY_GROUP },
    [toggleAll],
  );

  useHotkeys(
    'shift+d',
    (event) => {
      event.preventDefault();
      if (canCancel) void cancelAll();
    },
    { description: 'Dismiss selected', metadata: HOTKEY_GROUP },
    [canCancel, cancelAll],
  );

  return (
    // border-l-2 transparent mirrors the row's left border so the bar's
    // checkbox column lines up horizontally with each row's checkbox.
    <div className="flex h-[3.25rem] items-center gap-2 border-l-2 border-l-transparent pl-3 pr-3">
      <div className="flex shrink-0 items-center pr-2">
        <Checkbox
          checked={allSelected ? true : someSelected ? 'indeterminate' : false}
          onCheckedChange={toggleAll}
          disabled={total === 0 || isCancelling}
          aria-label={allSelected ? 'Deselect all' : 'Select all'}
        />
      </div>
      {count > 0 && (
        <>
          <span className="text-sm font-medium text-foreground">
            {count} selected
          </span>
          <div className="mx-2 h-4 w-px bg-border" />
          <Button
            variant="ghost"
            size="sm"
            onClick={cancelAll}
            disabled={!canCancel}
          >
            {isCancelling ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <XCircle className="mr-1.5 h-3.5 w-3.5" />
            )}
            Dismiss
          </Button>
          {extraActions}
        </>
      )}
    </div>
  );
}
